import { Duration, formatISODuration } from 'date-fns';

import useDefectLocatiesApi from '@/hooks/useDefectLocatiesApi';
import { managedPageQueryMeta, throwIfResponseNotOk } from '@/queries/queryErrorHandling';
import { Defect } from '@/types/Defecten';
import { LocatieVoorEenheden } from '@/types/LocatieVoorEenheden';
import { QueryFunction, useQuery } from '@tanstack/react-query';

import { useFetchWithAuth } from '../useFetchWithAuth';

const useGetDefectenWithLocation = (
  isoDuration: Partial<Omit<Duration, 'years' | 'months'>> = { days: 3 },
  refreshInterval?: number,
) => {
  const { getLocatiesVoorEenheden } = useDefectLocatiesApi(true);
  const { fetchWithAuth } = useFetchWithAuth();

  const fetcher: QueryFunction<{ defecten: Defect[]; locatieVoorEenheden: LocatieVoorEenheden[] }> = async ({
    signal,
  }) => {
    const isDefaultDuration = JSON.stringify(isoDuration) === JSON.stringify({ days: 3 });
    const url = isDefaultDuration
      ? `/defecten`
      : `/defecten?periode=${formatISODuration(isoDuration).replace('0M', '').replace('0Y', '')}`;
    const response = await fetchWithAuth(url, { signal });
    throwIfResponseNotOk(response, `Ophalen van defecten is mislukt (${response.status}).`);

    const defecten: Defect[] = (await response.json()) ?? [];
    const uniekeEenheden: string[] = Array.from(new Set(defecten?.map((def) => def.eenheidNummer) ?? []));
    const locatieVoorEenheden: LocatieVoorEenheden[] = [];
    for (let i = 0; i < uniekeEenheden.length; i += 200) {
      locatieVoorEenheden.push(...(await getLocatiesVoorEenheden(uniekeEenheden.slice(i, i + 200))));
    }
    return { defecten, locatieVoorEenheden };
  };

  return useQuery({
    queryKey: ['alleDefectenMetLocatie'],
    queryFn: fetcher,
    refetchInterval: refreshInterval,
    meta: managedPageQueryMeta,
  });
};
export default useGetDefectenWithLocation;
